import classnames from 'classnames/bind';
import EmailValidator from 'email-validator';
import PropTypes from 'prop-types';
import React from 'react';

import FloatingLabelInput from 'presentation/components/FloatingLabelInput';
import Header from 'presentation/components/Header';

import styles from 'presentation/components/styles/Onboarding.css';
let cx = classnames.bind(styles);

class Onboarding extends React.Component {
  constructor(props) {
    super(props);

    const onboardingPropTypes = {
      onSubmit: PropTypes.func.isRequired,
      error: PropTypes.string,
      hasEmailError: PropTypes.bool,
    };
    PropTypes.checkPropTypes(onboardingPropTypes, props, 'prop', 'Onboarding');

    this.state = {
      name: null,
      hasNameError: false,
      email: null,
      hasEmailError: props.hasEmailError || false,
      error: props.error || null,
      isSubmitting: false,
    };

    this.handleNameChange = this.handleNameChange.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.validate = this.validate.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    if (nextProps.hasEmailError) {
      return {
        error: nextProps.error,
        hasEmailError: nextProps.hasEmailError || false,
      };
    }
    return { isSubmitting: false };
  }

  handleNameChange(ev) {
    this.setState({ name: ev.target.value, hasNameError: false, error: null });
  }

  handleEmailChange(ev) {
    this.setState({ email: ev.target.value, hasEmailError: false, error: null });
  }

  handleKeyPress(ev) {
    if (this.state.isSubmitting) {
      return;
    }
    let charCode = ev.which || ev.keyCode;
    if (charCode === 13) {
      this.handleSubmit();
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  }

  handleSubmit() {
    this.setState({ isSubmitting: true });
    if (this.validate()) {
      this.props.onSubmit(this.state.name.trim(), this.state.email.trim());
    } else {
      this.setState({ isSubmitting: false });
    }
  }

  validate() {
    let hasValidFields = true;
    let nameError = getNameError(this.state.name);
    let emailError = getEmailError(this.state.email);

    if (nameError && emailError) {
      hasValidFields = false;
      this.setState({ hasNameError: true, hasEmailError: true, error: 'Can you try your name and email again?' });

      // special case
      if (nameError === 'Your name must be less than 100 characters.') {
        this.setState({
          error: 'Can you try your name and email again? Remember, your name must be less than 100 characters.',
        });
      }
    } else if (nameError) {
      hasValidFields = false;
      this.setState({ hasNameError: true, error: nameError });
    } else if (emailError) {
      hasValidFields = false;
      this.setState({ hasEmailError: true, error: emailError });
    }

    return hasValidFields;
  }

  render() {
    let aId = this.state.error ? 'gladlyChatOnboardingError' : 'gladlyChatOnboardingWelcome';

    let submitButtonClasses = cx(
      styles.onboarding_submitButton,
      'onboarding_submitButton',
      'onboarding_submitButton_selector'
    );
    let onboardingPaneClasses = cx(styles.onboarding, 'onboarding_selector');

    let onboardingMessageClasses = cx('onboarding_message', {
      onboarding_error: this.state.error,
    });

    let inputBoxNameClasses = cx('onboarding_inputBox_name', 'onboarding_inputBox', {
      onboarding_inputBox_error: this.state.hasNameError,
    });

    let inputBoxEmailClasses = cx('onboarding_inputBox_email', 'onboarding_inputBox', {
      onboarding_inputBox_error: this.state.hasEmailError,
    });

    return (
      <div className={onboardingPaneClasses} data-aid="gladlyChatOnboardingPane">
        <Header isOnboarding title={this.props.header} />
        <div className={styles.onboarding_body}>
          <div className={onboardingMessageClasses} data-aid={aId}>
            {this.state.error ? this.state.error : this.props.introMessage}
          </div>
          <FloatingLabelInput
            aria-label="name"
            id="id_name"
            role="form"
            className={inputBoxNameClasses}
            label={this.props.nameField}
            onChange={this.handleNameChange}
            onKeyPress={this.handleKeyPress}
            shrink={70}
            value={this.state.name}
            data-aid="gladlyChatOnboardingNameInput"
            type="text"
          />
          <FloatingLabelInput
            aria-label="email"
            id="id_email"
            role="form"
            className={inputBoxEmailClasses}
            type="email"
            label={this.props.emailField}
            onChange={this.handleEmailChange}
            onKeyPress={this.handleKeyPress}
            shrink={70}
            value={this.state.email}
            data-aid="gladlyChatOnboardingEmailInput"
          />
          <div
            tabIndex="0"
            role="button"
            className={styles.onboarding_submitButton_wrapper}
            onKeyDown={this.handleKeyPress}
          >
            <button
              aria-label="submitButton"
              disabled={this.state.isSubmitting}
              className={submitButtonClasses}
              onClick={this.handleSubmit}
              onKeyDown={this.handleKeyPress}
              data-aid="gladlyChatOnboardingSubmitButton"
              tabIndex="-1"
            >
              {this.props.submitButton}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

Onboarding.defaultProps = {
  header: "Let's Chat",
  introMessage: 'Hi there! Tell us a bit about yourself.',
  nameField: 'Your name',
  emailField: 'Your email',
  submitButton: "Let's Go",
};

export default Onboarding;

function getNameError(name) {
  let nameError = null;
  if (!name) {
    nameError = 'Please tell us your name.';
  } else if (name.trim().length > 100) {
    nameError = 'Your name must be less than 100 characters.';
  }
  return nameError;
}

function getEmailError(email) {
  let emailError = null;
  if (!email) {
    emailError = 'Please tell us your email.';
  } else if (!isValidEmail(email.trim())) {
    emailError = 'Try your email again.';
  }
  return emailError;
}

function isValidEmail(email) {
  return EmailValidator.validate(email);
}
